import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { AlertError } from "./Alert";
import { useTranslation } from "react-i18next";
import { SSE } from "./stream";
import tagManager from "./tags";
import posthog from "posthog-js";
import { ChatbotMessageChunk, AnyMessage } from "./types";
import { store, useAppDispatch, useAppSelector } from "../store";
import {
  useCurrentFrameSettings,
  usePrompt,
  useOnSignal,
  useCurrentFrameLocale,
  useLauncher,
  useIsFullscreen,
  useReduxStateChange,
} from "../hooks";
import {
  finishStreamingAndUpsertConversations,
  getPingRequestFromState,
} from "../actions/sharedActions";
import { setUserError, updateUserFromPing } from "../slices/userSlice";
import { Prompt } from "./Prompt";
import { MessageItem } from "./MessageItem";
import { useSyncSuggestions } from "../hooks";
import {
  clearChat,
  initialMessagesToChatMessages,
  prependInitialMessages,
  selectApiError,
  selectConversationId,
  selectIsLoading,
  selectMessages,
  sendMessage,
  setApiError,
  setConversationId,
  setInitialMessages,
  setMessages,
  updateStreamingResponse,
} from "../slices/chatSlice";
import { useRouter } from "../router";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { useFramePubSub } from "../pubsub";
import useExclusive from "./useExclusive";
import deepEqual from "fast-deep-equal";
import { isIframe } from "../PositionMode";
import { setAttachments, setQuickReplies } from "../slices/promptSlice";
import { Toaster } from "sonner";
import { FileData } from "../types/api";

// TODO(liamvdv): abort handler for request (show "Abort request" button).
// TOOD(liamvdv): timeout request with correct UI updates.

export function ThinkingDot() {
  const { t } = useTranslation();
  const [dots, setDots] = useState("");
  const [showThinking, setShowThinking] = useState(false);

  useEffect(() => {
    // Animate Dots
    const interval = setInterval(() => {
      setDots((prevDots) => {
        if (prevDots === "...") return "";
        return prevDots + ".";
      });
    }, 250); // 1000/4 for quarter-second intervals

    // Switch to "thinking" after 1.5 seconds
    const timeout = setTimeout(() => {
      setShowThinking(true);
    }, 1500);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, []);

  const displayText = `${t(showThinking ? "thinking" : "understanding")} ${dots}`;

  return (
    <div className="ml-2 p-2 flex flex-row items-center gap-2">
      <div className="relative inline-flex">
        <div className="w-3 h-3 bg-brand rounded-full"></div>
        <div className="w-3 h-3 bg-brand rounded-full absolute top-0 left-0 animate-ping"></div>
        <div className="w-3 h-3 bg-brand rounded-full absolute top-0 left-0 animate-pulse"></div>
      </div>
      <div>
        <span className="text-sm text-slate-600">{displayText}</span>
      </div>
    </div>
  );
}

function Messages({
  messages,
  isLoading,
}: {
  messages: AnyMessage[];
  isLoading: boolean;
}) {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    // if embed on page via iframe, this scrolls the parent window https://issues.chromium.org/issues/41375023.
    // would move the whole page on initial load.
    if (!isIframe())
      lastMessageRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // scroll to bottom on new messages once.
  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  return (
    <div>
      <div
        ref={messagesContainerRef}
        className="flex flex-col gap-3 max-w-3xl pt-2"
        // onScroll={() => isScrolledToBottom()}
      >
        {messages.map((message, i) => (
          <MessageItem key={i} message={message} />
        ))}
        {isLoading && <ThinkingDot />}
        <div ref={lastMessageRef} />
      </div>
    </div>
  );
}

function setWebsiteContextTags() {
  const page = window.location.href;
  const domain = window.location.hostname;
  const ignore = ["chat.botbrains.io", "staging.chat.botbrains.io"];
  if (ignore.some((i) => domain.includes(i))) return;
  tagManager.setTags({
    "chat:website.url": page,
  });
}

function Layout({
  secondaryAction,
  children,
}: {
  secondaryAction: "restart" | "none" | "toConversations";
  children: React.ReactNode;
}) {
  // NOTE(liamvdv): I'm aware that if isHeaderless, users cannot refresh the chat [closing is not important in fullscreen mode]
  //                OMR Edu is the only main user here.
  const { data: settings } = useCurrentFrameSettings();
  const isGenerating = useAppSelector((state) => state.chat.isLoading);
  const { frameId, mode } = useAppSelector((state) => state.config.data);
  const isHeaderless = mode.includes("headerless");
  const { navigate } = useRouter();
  const dispatch = useAppDispatch();
  const { toggle: onClose } = useLauncher();
  const refreshChat = () => dispatch(clearChat());

  const options = {
    restart: { icon: <RefreshCw className="w-5 h-5" />, action: refreshChat },
    toConversations: {
      icon: <ArrowLeft className="w-5 h-5" />,
      action: () => navigate(`/frames/${frameId}/conversations`),
    },
    none: { icon: undefined, action: undefined },
  };
  const { icon, action } = options[secondaryAction];
  const [hasError, setHasError] = useState<boolean>(false);

  const logo = (
    <div className="flex items-center">
      {typeof settings.header_image_url === "string" && !hasError ? (
        <img
          src={settings.header_image_url}
          alt="Logo"
          className="object-contain h-12 max-w-48"
          onError={() => setHasError(true)}
        />
      ) : null}
    </div>
  );

  const button = (
    label: string,
    icon: ReactNode | undefined,
    action: undefined | (() => void),
    pos: "left" | "right",
    disabled?: boolean
  ) => {
    return icon && action ? (
      <button
        onClick={action}
        className={`absolute ${pos == "left" ? "left-3" : "right-3"} top-2.5 p-3 text-header-close hover:bg-header-bg-hover rounded transition-colors disabled:cursor-not-allowed focus:outline-brand`}
        aria-label={label}
        disabled={disabled ?? false}
      >
        {icon}
      </button>
    ) : (
      <></>
    );
  };

  const closeButton = button(
    "Close Button",
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 4 4 20"></path>
      <path d="m4 4 16 16"></path>
    </svg>,
    onClose,
    "right"
  );

  const inFullscreen = useIsFullscreen();
  const isModeFullscreen = mode.includes("fullscreen");
  let left, right;
  if (inFullscreen) {
    if (!isModeFullscreen) right = closeButton;
    if (secondaryAction === "restart") {
      left = button("Restart Chat", icon, action, "left", isGenerating);
    } else if (secondaryAction == "toConversations") {
      left = button(
        "Back to Conversation List",
        icon,
        action,
        "left",
        isGenerating
      );
    } else if (secondaryAction == "none") {
      left = undefined;
    } else {
      throw new Error("Invalid secondaryAction");
    }
  } else {
    if (secondaryAction === "restart") {
      left = undefined;
      right = button("Restart Chat", icon, action, "right", isGenerating);
    } else if (secondaryAction == "toConversations") {
      left = button(
        "Back to Conversation List",
        icon,
        action,
        "left",
        isGenerating
      );
      right = undefined;
    } else if (secondaryAction == "none") {
      left = undefined;
      right = undefined;
    } else {
      throw new Error("Invalid secondaryAction");
    }
  }

  return (
    <div className="w-full h-full relative font-sans bg-chat overscroll-hidden">
      <div className="relative h-full w-full transition-width flex flex-col overflow-hidden items-stretch flex-1">
        <div className="flex flex-col h-full">
          {!isHeaderless && (
            <header className="relative flex shrink-0 items-center justify-center h-16 bg-header-bg px-[20px] py-2">
              {/* Action Button */}
              {left && left}
              {/* Centered Logo */}
              {logo}
              {/* Close Button */}
              {right && right}
            </header>
          )}
          <Toaster position="top-center" visibleToasts={11} richColors />
          {children}
        </div>
      </div>
    </div>
  );
}
export { Layout };

function isNetworkError(error: unknown): boolean {
  // Check if it's a TypeError (fetch errors)
  if (error instanceof TypeError) {
    return true;
  }

  // Check for offline/network disconnected
  if (
    error instanceof DOMException &&
    ["NetworkError", "AbortError", "InternetDisconnectedError"].includes(
      error.name
    )
  ) {
    return true;
  }

  // For cross-browser support, check if error has a name property
  if (error && typeof error === "object" && "name" in error) {
    const errorName = (error as { name: string }).name;
    return errorName.includes("Network") || errorName.includes("Connection");
  }

  return false;
}

const OtherTabGeneratingBlocker = () => {
  const { t } = useTranslation();
  return (
    <div className="py-2 px-[20px]">
      <div>{t("noConversationBranching")}</div>
    </div>
  );
};

// NOTE(liamvdv): fix: this is a hack to check if the first message is a user message.
//                The next generation of the Message History API will also store the initial message.
//                We need to change our behaviour in some spots to still work with v1.
const isV1MessageHistory = (messages: AnyMessage[]) =>
  messages.findIndex((m) => m.role === "user") === 0;

export default function Chat() {
  const dispatch = useAppDispatch();
  const { locale, status: localeStatus } = useCurrentFrameLocale();
  const { publish } = useFramePubSub();
  useSyncSuggestions();

  const { data: config, status } = useAppSelector((state) => state.config);
  const { data: conversations, status: conversationsStatus } = useAppSelector(
    (state) => state.conversations
  );
  const suggestions = useAppSelector((state) => state.prompt.suggestions) || [];
  const messages = useAppSelector(selectMessages);
  const conversationId = useAppSelector(selectConversationId);
  const attachments = useAppSelector((state) => state.prompt.files);
  const isLoading = useAppSelector(selectIsLoading);
  const apiError = useAppSelector(selectApiError);

  const { isLocked, withLock } = useExclusive(config.frameId, conversationId);
  const {
    text: prompt,
    setText: setPrompt,
    isDisabled,
    isShow: isShowPrompt,
  } = usePrompt();

  // *********************** Handle Locale Changes ************************** //
  // If no conversation started yet AND the messages are just the initial messages, update the intro message
  // if the locale changes.
  const allInitial =
    messages.every((m) => !!m.isInitial) && messages.length > 0;
  useReduxStateChange(
    (state) => state.config.data.locale,
    () => {
      if (allInitial) dispatch(setInitialMessages(locale?.messages ?? []));
    }
  );

  // *********************** Set Initial Messages *************************** //
  // NOTE(liamvdv): backwards compatibility for missing initial messages.
  //
  // Set message to initial messages if no messages are present yet.
  const isMissingIntroMessage =
    messages.length === 0 || isV1MessageHistory(messages);
  useEffect(() => {
    if (localeStatus === "succeeded" && isMissingIntroMessage)
      dispatch(prependInitialMessages(locale?.messages ?? []));
  }, [localeStatus, locale?.messages, isMissingIntroMessage]);

  // ******************* Sync Messages from API ***************************** //
  // NOTE(liamvdv): We pull in background updates only when not loading (still a race condition with message rating possible.)
  const apiConversation = conversations.find(
    (c) => c.conversation_id === conversationId
  );
  const localCoreMessages = messages.filter((m) => !m.isInitial);
  const remoteCoreMessages =
    apiConversation?.messages.filter((m) => !m.isInitial) ?? [];
  const isUpdateAvailable =
    // remote can also have updated metadata; thus larger equal.
    localCoreMessages.length <= remoteCoreMessages.length &&
    !deepEqual(remoteCoreMessages, localCoreMessages);
  useEffect(() => {
    // do not sync conversation if currently generating.
    if (isLoading) return;
    if (!apiConversation) return;
    if (isUpdateAvailable) {
      // NOTE(liamvdv): we want to keep newly suggested messages at the end of the input.
      const tailIntro = messages.slice(
        messages.findLastIndex((m) => !m.isInitial) + 1
      );
      // NOTE(liamvdv): backwards compatibility for missing initial messages in v1 history api.
      const apiMessages = isV1MessageHistory(apiConversation.messages)
        ? [
            ...initialMessagesToChatMessages(locale?.messages ?? []),
            ...apiConversation.messages,
          ]
        : apiConversation.messages;
      const mergedMessages = [...apiMessages, ...tailIntro];
      dispatch(setMessages(mergedMessages));
    }
  }, [conversationsStatus, apiConversation, isUpdateAvailable, isLoading]);

  const handleSend = useCallback(
    (value: string, files?: FileData[]) => {
      if (isDisabled) {
        window.alert("Send is not possible in this view.");
        return;
      }
      setPrompt("");
      dispatch(setQuickReplies([]));
      const effectiveAttachments = files ?? attachments;
      dispatch(setAttachments([]));
      if (!posthog.sessionRecordingStarted()) posthog.startSessionRecording();

      const pingRequestData = getPingRequestFromState(store.getState());
      setWebsiteContextTags();

      performance.mark("chat_stream_start");

      const generator = dispatch(
        sendMessage({
          value,
          attachments: effectiveAttachments,
          conversationId,
          config,
          extraHeaders: [],
          pingRequestData,
          previousMessages: messages,
          tags: tagManager.getTags(),
        })
      ).then(({ payload: { url, headers, payload } }: any) => {
        let text = "";
        let analyticsProps = {
          isSuggestion: suggestions.includes(value),
          frameId: config.frameId,
          error: undefined as string | undefined,
          messageId: undefined as any,
        };

        const onData = ({
          data,
          headers,
        }: {
          data: ChatbotMessageChunk;
          headers: Headers;
          cancel: () => void;
        }) => {
          // NOTE(liamvdv): hack to share the current message_id with the onEnd function.
          analyticsProps.messageId = data.metadata.message_id;

          dispatch(setConversationId(data.metadata.conversation_id));

          dispatch(
            updateUserFromPing({
              frameId: config.frameId,
              headers: {
                session_id: headers.get("X-Session-Id") ?? undefined,
                user_id: headers.get("X-User-Id") ?? undefined,
                device_id: pingRequestData.device_id,
              },
              user_data: data.user_data,
            })
          );

          if (data.text !== undefined) {
            text += data.text;
          }

          dispatch(
            updateStreamingResponse({
              text,
              metadata: data.metadata,
              messageId: data.metadata.message_id,
            })
          );
        };

        const onError = (error: unknown) => {
          console.error(error);
          let message =
            "Sorry, we encountered an error. Please reload the browser page.";
          let errorData;
          if (isNetworkError(error)) {
            message =
              "Internet connection lost. Please check your connection. Reload the browser page to try again.";
          } else if (error instanceof Error) {
            message = error.message;
            try {
              errorData = JSON.parse(error.message);
            } catch (e) {
              console.error("Error parsing error message:", e);
            }
          } else if (typeof error === "string") {
            message = error;
          }
          // NOTE(memben): setting duplicate errors to avoid refactor
          //               feel free to unify these error states in the future
          dispatch(setApiError(message));
          dispatch(setUserError(errorData));

          analyticsProps.error = message;
        };

        const onEnd = (status: any) => {
          performance.mark("chat_stream_end");
          const measure = performance.measure(
            "chat_message_duration",
            "chat_stream_start",
            "chat_stream_end"
          );

          const props = {
            ...analyticsProps,
            stream: true,
            status,
            startTime: measure.startTime,
            duration: measure.duration,
            textLength: text.length,
            conversationId: conversationId,
          };

          posthog.capture("api_chat_message", props);
          if (status === "succeeded") {
            dispatch(
              finishStreamingAndUpsertConversations({
                messageId: analyticsProps.messageId,
              })
            );
          } else {
            console.error(
              "LIKELY_ERROR: message not ended successfully, did not finish streaming (message still isGenerating: true)"
            );
          }
          publish({
            type: "message_end",
            message_id: analyticsProps.messageId,
            conversation_id: conversationId!,
          });
        };

        publish({
          type: "message_start",
          conversation_id: conversationId,
        });
        return SSE(url, {
          headers: new Headers(headers),
          payload,
          onData,
          onError,
          onEnd,
        });
      });
      withLock(generator);
    },
    [messages, conversationId, suggestions, dispatch, attachments]
  );

  useOnSignal("chat.send", () => handleSend(prompt));

  // by default, target is "restart"
  const target = useAppSelector(
    (state) => state.config.data.experimentalListConversations
  )
    ? "toConversations"
    : "restart";

  const quickReplies =
    useAppSelector((state) => state.prompt.quickReplies) || [];

  // We do not want to show quick replies if
  // - we are generating
  // - we do NOT have an existing conversation (use suggestions)
  // - handoff or another mechanism is active such that the prompt is hidden.
  const showQuickReplies =
    messages.length !== 1 && quickReplies.length > 0 && !isLoading; // TODO(liamvdv): add API call to generate quick replies / use messages[messages.length-1].metadata.quick_replies
  // We do not want to show suggestions if
  // - we are generating
  // - we have an existing conversation
  const showSuggestions = messages.length === 1 && suggestions.length > 0;

  return (
    <Layout secondaryAction={target}>
      <div className="flex-grow overflow-hidden px-[20px]">
        <div className="h-full max-w-3xl mx-auto overscroll-y-contain overflow-y-auto no-scrollbar scrollable">
          <Messages messages={messages} isLoading={isLoading} />
        </div>
      </div>
      {apiError && (
        <div className="py-2 px-[20px]">
          <AlertError title={"Sorry, we encountered an error"}>
            {apiError}
          </AlertError>
        </div>
      )}
      {isLocked && <OtherTabGeneratingBlocker />}

      {isShowPrompt && (
        <div className="flex-shrink-0 w-full max-w-3xl mx-auto px-[20px]">
          <SuggestionsOrQuickReplies
            suggestions={suggestions}
            showSuggestions={showSuggestions}
            quickReplies={quickReplies}
            showQuickReplies={showQuickReplies}
            isDisabled={isDisabled}
            onSend={handleSend}
          />

          <div className="w-full">
            <Prompt
              value={prompt}
              isLoading={isLoading || isLocked}
              isDisabled={isDisabled}
              setValue={setPrompt}
              onSubmit={handleSend}
            />
          </div>
        </div>
      )}
    </Layout>
  );
}

function SuggestionsOrQuickReplies({
  suggestions,
  showSuggestions,
  quickReplies,
  showQuickReplies,
  isDisabled,
  onSend,
}: {
  suggestions: string[];
  showSuggestions: boolean;
  quickReplies: string[];
  showQuickReplies: boolean;
  isDisabled: boolean;
  onSend: (value: string) => void;
}) {
  const { t } = useTranslation();
  if (showSuggestions)
    return (
      <div className="mb-2.5">
        <p className="text-sm text-chat-text-light text-center mb-2">
          {t("suggestions")}
        </p>
        {/* NOTE(liamvdv): we want to fill up bottom to top. but keep the visual order. Thus the doubel rotates, flex-wrap-reverse would break human understood order behaviour. */}
        <div
          className="flex flex-wrap gap-2 justify-start"
          style={{ transform: "rotate(180deg)" }}
        >
          {[...suggestions].reverse().map((suggestion) => (
            <button
              disabled={isDisabled}
              key={suggestion}
              onClick={() => onSend(suggestion)}
              className="border-chat text-chat bg-brand hover:bg-brand-hover px-4 py-1.5 rounded-[24px] text-left disabled:cursor-not-allowed"
              style={{ transform: "rotate(180deg)" }}
            >
              <p className="text-sm">{suggestion}</p>
            </button>
          ))}
        </div>
      </div>
    );
  if (showQuickReplies)
    return (
      <div className="mb-2.5">
        <div className="flex flex-wrap gap-2 justify-end">
          {quickReplies.map((qr) => (
            <button
              disabled={isDisabled}
              key={qr}
              onClick={() => onSend(qr)}
              className="border-chat text-chat bg-brand hover:bg-brand-hover px-4 py-1.5 rounded-[24px] text-left disabled:cursor-not-allowed"
            >
              <p className="text-sm">{qr}</p>
            </button>
          ))}
        </div>
      </div>
    );
  return null;
}
